import { Directive, EventEmitter, inject, InjectionToken, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  ExpressCheckoutContext,
  ExpressCheckoutHandlers,
  ExpressCheckoutProvider,
  ExpressCheckoutResult,
  ExpressCheckoutService,
  LoggingService,
  ProviderActionModel,
  ProviderName,
} from '@fgb/core';

export interface ExpressCheckoutSettings {
  settings: Record<string, any>;
}

/**
 * Resolve the wallet provider sub-id from the customer's device: Apple Pay on Apple mobile/tablet
 * (iPhone / iPad / iPod), Google Pay on Android. Returns undefined on desktop (including desktop
 * Mac), so no device wallet sub-id is sent. Passed to init as `providerSubId`. Standalone (not a
 * method) so the container can reuse it for its init pass without subclassing the base.
 */
export function resolveDeviceProviderSubId(): ProviderName | undefined {
  const ua = navigator?.userAgent ?? '';

  // iPadOS Safari reports as "Macintosh"; distinguish a touch iPad from a desktop Mac.
  const isIpadOs = /Macintosh/i.test(ua) && (navigator?.maxTouchPoints ?? 0) > 1;
  const isAppleMobile = /iPhone|iPad|iPod/i.test(ua) || isIpadOs;
  if (isAppleMobile) {
    return ProviderName.Apple;
  }

  if (/Android/i.test(ua)) {
    return ProviderName.Google;
  }

  // Desktop (or unrecognised) — send no device wallet sub-id.
  return undefined;
}

/** Maps the raw express-checkout init response into the settings a provider needs (script urls, etc). */
export type ExpressCheckoutSettingsBuilder = (response: any) => ExpressCheckoutSettings;

/**
 * Per-provider mapper that turns the init response into ExpressCheckoutSettings. Each provider
 * component provides its own in its `@Component.providers`; the base injects it from the provider's
 * own element injector. Defaults to null — the base then does no response-derived setup.
 */
export const EXPRESS_CHECKOUT_SETTINGS = new InjectionToken<ExpressCheckoutSettingsBuilder | null>('expressCheckoutSettings', {
  providedIn: 'root',
  factory: () => null,
});

export interface BaseExpressCheckoutService {
  initialize(_: any): Promise<boolean>;
  /**
   * Run the provider's checkout/submit flow (SDK calls) for the given context and report the outcome
   * via the handlers. Optional: providers whose flow is driven elsewhere (e.g. FreedomPay's iframe
   * postMessage) may omit it.
   */
  submit?(context: ExpressCheckoutContext, handlers: ExpressCheckoutHandlers): Promise<void> | void;
  /**
   * Release the provider SDK's resources. Optional: providers whose SDK has nothing to release may
   * omit it. Called from the base component's ngOnDestroy, so a provider that is re-rendered (the
   * container rebuilds its providers whenever the orderId changes) does not leak an SDK instance,
   * its iframes, or its listeners.
   */
  teardown?(): Promise<void> | void;
}

/**
 * Base class for express checkout provider components (e.g. Paze). Extend this when creating a new
 * express checkout provider so the ExpressCheckoutComponent container can render and drive it
 * uniformly. The container sets `context` and subscribes to `result` / `incomplete`.
 */
@Directive()
export abstract class BaseExpressCheckoutComponent implements ExpressCheckoutProvider, OnInit, OnDestroy {
  @Input() context: ExpressCheckoutContext;

  /** Emitted when the express checkout finishes (success or failure). */
  @Output() result = new EventEmitter<ExpressCheckoutResult>();

  /** Emitted when the customer is ineligible / cancels, so the host can restore the native flow. */
  @Output() incomplete = new EventEmitter<ExpressCheckoutResult>();

  private expressCheckoutService = inject(ExpressCheckoutService);
  protected logService = inject(LoggingService);
  private buildSettings = inject(EXPRESS_CHECKOUT_SETTINGS);

  /** Whether the SDK is ready and the provider can be offered. Controls button visibility. */
  ready = false;

  /** Whether a submit attempt is currently in flight. Controls button disabled state. */
  processing = false;

  /** Settings the provider mapper extracted from the last init response (script urls, etc). */
  protected config: ExpressCheckoutSettings | null = null;

  constructor(
    private _providerId: ProviderName,
    private service: BaseExpressCheckoutService
  ) {}

  /**
   * Optional hook run after the provider has initialised successfully. Override in a component that
   * needs to act on init (e.g. FreedomPay embeds its iframe here). Default is a no-op, so components
   * that do nothing post-init (e.g. Paze, which waits for a button press) can leave it unimplemented.
   */
  protected onInitialized(_: ProviderActionModel): void | Promise<void> {}

  /**
   * Optional hook run when initialisation does not end in a ready provider (the init request failed
   * or threw, or the SDK refused to initialise). Override in a component that shows pending UI
   * before init resolves (e.g. FreedomPay clears its loading state here). Default no-op.
   */
  protected onInitializeFailed(): void | Promise<void> {}

  /**
   * Optional hook run after a submit attempt completes (success or failure). Override in a component
   * that needs to react to submission. Default is a no-op.
   */
  protected onSubmitted(): void | Promise<void> {}

  /**
   * Optional hook run when the provider reports a completed result (in addition to emitting `result`).
   * Override in a component that needs to react to a successful/finished checkout. Default no-op.
   */
  protected onCompleted(_result: ExpressCheckoutResult): void | Promise<void> {}

  /**
   * Optional hook run when the provider reports an incomplete outcome (in addition to emitting
   * `incomplete`). Override in a component that needs to react to ineligible/cancelled. Default no-op.
   */
  protected onIncomplete(_result: ExpressCheckoutResult): void | Promise<void> {}

  async ngOnInit(): Promise<void> {
    await this.initialize();
  }

  /**
   * Release the provider SDK when the component goes away. A subclass that needs its own cleanup
   * must override this AND call `super.ngOnDestroy()`, or the SDK teardown is skipped.
   */
  ngOnDestroy(): void {
    this.ready = false;
    // Fire and forget: teardown failures are logged by the service, and nothing can act on them
    // once the component is being destroyed.
    void this.service.teardown?.();
  }

  /**
   * Trigger the provider's checkout/submit flow. Called by the provider's UI (e.g. a wallet button)
   * once the customer is ready. Guards on ready/processing/context, delegates the SDK flow to the
   * service (which reports via handlers -> result/incomplete), then runs the onSubmitted() hook.
   */
  async submit(): Promise<void> {
    if (!this.ready || this.processing || !this.context) {
      return;
    }

    this.processing = true;
    try {
      // The service owns the checkout -> complete -> submit flow; we forward its outcome to the
      // outputs and to the optional component hooks.
      await this.service.submit?.(this.context, {
        onResult: (result) => {
          this.result.emit(result);
          void this.onCompleted(result);
        },
        onIncomplete: (result) => {
          this.incomplete.emit(result);
          void this.onIncomplete(result);
        },
      });
    } finally {
      this.processing = false;
      await this.onSubmitted();
    }
  }

  /**
   * Self-init: fetch this provider's express checkout init response from the backend, then consume
   * it. Used when the provider runs standalone (no container feeding `initResponse`). The container
   * flow calls `expressCheckoutService.initExpress` itself and feeds the result via `initResponse`,
   * so this path is skipped there.
   */
  async initialize(): Promise<void> {
    try {
      // Route to the wallet matching the customer's device: Apple Pay on Apple, else Google Pay.
      const providerSubId = this.resolveDeviceProviderSubId();
      const response = await this.expressCheckoutService.initExpress(this.context, this._providerId, providerSubId);

      if (!response?.Success) {
        this.logService.error('Express checkout init failed:', this._providerId, response?.ErrorMessage);
        this.ready = false;
        return;
      }

      const orderId = (response as any).OrderId || '';
      if (orderId && !this.context?.orderId) {
        this.context = { ...this.context, orderId };
      }

      const action = response.ProviderActionInformation;
      // The provider component maps the raw response into the settings it needs (e.g. SDK script urls).
      const settings = this.extractSettings(action);
      const scripts = this.extractScripts(action);
      const config = this.buildSettings ? this.buildSettings(settings) : null;
      this.config = config;

      this.ready = await this.service.initialize({ settings: config?.settings ?? {}, scripts });
      if (!this.ready) {
        this.logService.error('SDK failed to initialize (check the SDK url and credentials)');
        return;
      }

      // The component's optional post-init hook (e.g. FreedomPay embeds its iframe here).
      await this.onInitialized(action);
    } catch (error) {
      // A rejected init request (network error, 500) must not escape ngOnInit as an unhandled
      // rejection; the provider simply never becomes ready and stays hidden.
      this.ready = false;
      this.logService.error('Express checkout init threw:', this._providerId, error);
    } finally {
      if (!this.ready) {
        void this.onInitializeFailed();
      }
    }
  }

  /** @see resolveDeviceProviderSubId — kept as a method for subclasses that override device routing. */
  protected resolveDeviceProviderSubId(): ProviderName | undefined {
    return resolveDeviceProviderSubId();
  }

  /**
   * Extract the merchant settings from a provider action: the Resources entry whose
   * ResourceContentType is 'properties'. Returns null when no such resource is present.
   */
  protected extractSettings(action: ProviderActionModel): Record<string, any> | null {
    const propertiesResource = action?.Resources?.find((resource) => resource?.ResourceContentType === 'properties');
    return propertiesResource?.Properties ?? null;
  }

  /**
   * Extract the SDK script urls from a provider action. Prefers the action's top-level `Url` when
   * present; otherwise falls back to the `Url` of every Resources entry whose ResourceContentType
   * is 'text/javascript'.
   */
  protected extractScripts(action: ProviderActionModel): string[] {
    if (action?.Url) {
      return [action.Url];
    }
    return (action?.Resources ?? [])
      .filter((resource) => resource?.ResourceContentType === 'text/javascript')
      .map((resource) => resource?.Url)
      .filter((url): url is string => !!url);
  }
}
